import { ICertificate } from "../../../../Global/Base/Crypto/ISignatureInfo";
import { Event } from "../../../../Global/Base/Events";
import { CultureInfo } from "../../../../Global/Dom/CultureInfo";
import { Localization } from "../../../../OW.Remoting/Remoting/Localization";
import { GenericControl } from "../../../UI.BaseControls/GenericControl";
import { createElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { CryptoInfo } from "../Helpers/CryptoInfo";
import { MessageWindow } from "../MessageWindow";
import { IContentBuilder } from "./IContentBuilder";
/**
 * Builder, který sestaví dialog výběru certifikátu k šifrování
 * @internal
 */
export class UserCertificateSelectorBuilder implements IContentBuilder {
    private _element: HTMLElement;
    private _inputCaption: HTMLLabelElement;
    private _parent: MessageWindow;
    // @ts-ignore // nevyužito, ale ponecháno
    private _textLocKey: string;
    private _fileElement: HTMLInputElement;
    private _certificateDetail: HTMLElement;
    private _textElement: HTMLDivElement;
    private _warningElement: HTMLDivElement;
    private _subTextElement: HTMLDivElement;
    private _topTextElement: HTMLDivElement;
    private _expandButton: HTMLDivElement;
    private _detail: HTMLElement;

    /**
     * Událost volána pri vyberu objektu
     */
    public selectedFileChange: Event<UserCertificateSelectorBuilder, File> = new Event<UserCertificateSelectorBuilder, File>();

    /**
     * Vytvoří dialog pro zadání hesla pro šifrování/dešifrování záznamu.
     * @param parent Instance MessageWindow.
     * @param textLocKey Lokalizační klíč, který bude zobrazen jako zpráva dialogu.
     * @param obj Refence objektu, který se má (de)šifrovat.
     * @param passwordDialogType Určuje k jakému účelu má být builder určen - k nastavení nebo ověření hesla
     */
    constructor(parent: MessageWindow, textLocKey: string) {
        this._textLocKey = textLocKey;
        this._parent = parent;

        this._fileElement = createElement({ type: "input[file]" }) as HTMLInputElement;
        this._fileElement.accept = ".cer, .der";

        GenericControl.registerEventListener(this._fileElement, "change", (e) => {
            e.stopPropagation();
            if (this._fileElement.files == undefined || this._fileElement.files.length == 0) {
                return;
            }
            if (this._parent.leftButton1) {
                this._parent.leftButton1.style.display = "none";
            }
            if (this._parent.leftButton2) {
                this._parent.leftButton2.style.display = "";
            }

            const selectedFile = this._fileElement.files[0];
            this.selectedFileChange.trigger(this, selectedFile);
        });
        //this.appendChild(this._fileElement);
    }

    public async createCertificateInfo(certificateInfo: ICertificate, showWarning: boolean = false): Promise<void> {
        if (!this._element)
            await this._build();

        this._parent.okButton.classList.remove("Disabled");
        this._parent.okButton.disabled = false;

        const culture = CultureInfo.getCultureInfo().name;

        this._parent.header = "";

        if (this._parent.leftButton1)
            this._parent.leftButton1.style.display = "none";
        if (this._parent.leftButton2)
            this._parent.leftButton2.style.display = "";

        this._topTextElement.innerText = CryptoInfo.formatDistinguishedNamePart(certificateInfo.subject, "CN");
        this._subTextElement.innerText = CryptoInfo.formatDistinguishedNamePart(certificateInfo.subject, "O");

        const dateFrom = new Date(certificateInfo.validFrom.toString()).toLocaleDateString(culture);
        const dateTo = new Date(certificateInfo.validTo.toString()).toLocaleDateString(culture);

        if (dateFrom && dateTo)
            this._expandButton.textContent = dateFrom + " - " + dateTo;
        else {
            Localization.getTextPromise("GUI-348247").then((value) => { //podrobnosti
                this._expandButton.textContent = value;
            });
        }
        this._expandButton.style.display = "";

        if (certificateInfo) {
            this._certificateDetail.removeChild(this._detail);
            this._detail = await CryptoInfo.createCertificateDetailBody(certificateInfo);
            this._certificateDetail.insertBefore(this._detail, this._warningElement);
        }

        this._warningElement.style.display = showWarning ? "" : "none";

        this._certificateDetail.style.display = "";
    }

    public async certificateNotSelected(): Promise<void> {
        this._warningElement.style.display = "none";
        this._expandButton.style.display = "none";
        this._certificateDetail.style.display = "none";
        if (this._parent.leftButton1)
            this._parent.leftButton1.style.display = "";
        if (this._parent.leftButton2)
            this._parent.leftButton2.style.display = "none";
        this._parent.okButton.classList.add("Disabled");
        this._parent.okButton.disabled = true;
    }

    private async _initCertificateDetail(): Promise<void> {
        this._certificateDetail = createElement({ classes: "item-with-detail" });

        this._textElement = document.createElement("div");
        this._textElement.classList.add("text");
        this._certificateDetail.appendChild(this._textElement);

        this._topTextElement = document.createElement("div");
        this._topTextElement.classList.add("top-text");
        this._textElement.appendChild(this._topTextElement);

        this._subTextElement = document.createElement("div");
        this._subTextElement.classList.add("sub-text");
        this._textElement.appendChild(this._subTextElement);

        this._expandButton = document.createElement("div");
        this._expandButton.classList.add("expand-btn");
        this._expandButton.style.display = "none";

        this._certificateDetail.appendChild(this._expandButton);

        this._detail = createElement({ classes: "detail" });

        this._certificateDetail.appendChild(this._detail);

        this._certificateDetail.addEventListener("click", (_e) => {
            this._certificateDetail.classList.toggle("expanded");
        });

        //this._warningElement = document.createElement({ classes: ["Text", "warning", "red", "Icon"], css: { display: "none" } });

        this._warningElement = document.createElement("div");
        this._warningElement.classList.add("text", "warning", "red", "Icon");
        this._warningElement.style.display = "none";

        Localization.getTextPromise("GUI-642367").then((value) => { // Chyba při kontrole vybraného certifikátu
            this._warningElement.textContent = value;
        });
        this._certificateDetail.appendChild(this._warningElement);

        this._element.appendChild(this._certificateDetail);
    }

    private async _build(): Promise<void> {
        if (!this._element) {
            const content = createElement();
            this._inputCaption = createElement({ classes: "Caption" }) as HTMLLabelElement;

            this._parent.left1Callback = async (): Promise<boolean> => {
                return await this._openFileClickedCallback();
            };
            this._parent.left2Callback = async (): Promise<boolean> => {
                return await this._openFileClickedCallback();
            };

            content.appendChild(this._inputCaption);

            this._element = content;

            this._initCertificateDetail();
        }
    }

    private async _openFileClickedCallback(): Promise<boolean> {
        this._fileElement.click();
        return false;
    }

    public async build(): Promise<HTMLElement> {
        await this._build();
        return this._element;
    }
}
